import {ExternalLinkIcon, StatusOnlineIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';

import {SYSTEM_HEALTH_DASHBOARD_URL} from '@/config/endpoints';

type SystemHealthBannerProps = {
  /** Why this page's reader would want the dashboard. One or two sentences. */
  reason: string;
};

/**
 * A note at the top of a page pointing to the system health dashboard, with a
 * page-specific reason for looking at it.
 *
 * Not `InfoBox`: that renders a loud blue `role="alert"` banner, which screen
 * readers announce on every page load. This is standing guidance, so it is a
 * plain, quiet note.
 */
export default function SystemHealthBanner({
  reason,
}: SystemHealthBannerProps): JSX.Element {
  return (
    <aside
      aria-label="System health"
      data-cy="system-health-banner"
      className="mt-4 flex gap-3 rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm dark:border-dark-navBorder dark:bg-dark-blueBg"
    >
      <StatusOnlineIcon
        className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600"
        aria-hidden="true"
      />
      <div className="flex flex-col gap-1">
        <p className="text-gray-700 dark:text-gray-200">{reason}</p>
        <p>
          <a
            href={SYSTEM_HEALTH_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="system-health-banner-link"
            className="inline-flex items-center gap-1 font-semibold text-cyan-700 hover:underline dark:text-[#0075FF]"
          >
            Open the system health dashboard
            <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
            In Datadog. You need a Datadog login.
          </span>
        </p>
      </div>
    </aside>
  );
}
